import { runInAction } from "mobx"

import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { TokenBlacklistChecker } from "@/domains/tokens/tokens-blacklist"
import { LcAddress } from "@/domains/core"
import { IEventBus } from "@/domains/events"
import { OrchestratedQuery } from "@/domains/library"
import { SwapCompletedEvent, SwapExecutionStartedEvent } from "@/domains/swap/events"
import { TokenApiFactory } from "@/domains/tokens/factories/token-api-factory"
import { TokenOnChainTransport } from "@/domains/tokens/token-onchain-transport"
import { TokenRepository } from "@/domains/tokens/token-repository"
import { TokenStore } from "@/domains/tokens/token-store"
import { WalletConnectionStore } from "@/domains/wallet"
import { WalletConnectedEvent, WalletDisconnectedEvent } from "@/domains/wallet/events"

import type { TokenApiDAO } from "./types"
import { ETH, staticTokens } from "./static-tokens"

export class TokenOrchestrator {
  private tokenSyncQuery: OrchestratedQuery<Record<string, TokenApiDAO>> | undefined
  private balanceSyncQuery: OrchestratedQuery<Map<string, bigint>> | undefined
  private nativeBalanceSyncQuery: OrchestratedQuery<bigint> | undefined
  private walletConnectedHandler: ((event: WalletConnectedEvent) => void) | undefined
  private walletDisconnectedHandler: ((event: WalletDisconnectedEvent) => void) | undefined
  private swapCompletedHandler: ((event: SwapCompletedEvent) => void) | undefined
  private swapExecutionStartedHandler: ((event: SwapExecutionStartedEvent) => void) | undefined
  // Store pre-swap balances keyed by quote ID (we'll use this to track which swap we're validating)
  private preSwapBalances = new Map<string, Map<string, bigint>>()

  constructor(
    private readonly tokenRepository: TokenRepository,
    private readonly walletStore: WalletConnectionStore,
    private readonly tokenOnChainTransport: TokenOnChainTransport,
    private readonly blacklistChecker: TokenBlacklistChecker,
    private readonly eventBus: IEventBus,
  ) {
    // Subscribe to wallet events
    this.walletConnectedHandler = this.handleWalletConnect.bind(this)
    this.walletDisconnectedHandler = this.handleWalletDisconnect.bind(this)
    this.eventBus.subscribe(WalletConnectedEvent.TYPE, this.walletConnectedHandler)
    this.eventBus.subscribe(WalletDisconnectedEvent.TYPE, this.walletDisconnectedHandler)
    this.swapCompletedHandler = this.handleSwapCompleted.bind(this)
    this.eventBus.subscribe(SwapCompletedEvent.TYPE, this.swapCompletedHandler)
    this.swapExecutionStartedHandler = this.handleSwapExecutionStarted.bind(this)
    this.eventBus.subscribe(SwapExecutionStartedEvent.TYPE, this.swapExecutionStartedHandler)
  }

  private handleWalletDisconnect(_event: WalletDisconnectedEvent): void {
    // console.log("token-orchestrator.handleWalletDisconnect", event.address)

    if (this.balanceSyncQuery) {
      this.balanceSyncQuery.stop()
    }
    if (this.nativeBalanceSyncQuery) {
      this.nativeBalanceSyncQuery.stop()
    }

    this.tokenRepository.clearAllBalances()
    ETH.updateBalance(0n)
  }

  private async handleWalletConnect(_event: WalletConnectedEvent): Promise<void> {
    // console.log("token-orchestrator.handleWalletConnect", event.address)

    if (this.balanceSyncQuery) {
      this.balanceSyncQuery.start()
    }
    if (this.nativeBalanceSyncQuery) {
      this.nativeBalanceSyncQuery.start()
    }
  }

  initialize(
    tokenSyncQuery: OrchestratedQuery<Record<string, TokenApiDAO>>,
    balanceSyncQuery: OrchestratedQuery<Map<string, bigint>>,
    nativeBalanceQuery: OrchestratedQuery<bigint>,
  ) {
    this.tokenSyncQuery = tokenSyncQuery
    this.balanceSyncQuery = balanceSyncQuery
    this.nativeBalanceSyncQuery = nativeBalanceQuery
  }

  get query(): OrchestratedQuery<Record<string, TokenApiDAO>> | undefined {
    return this.tokenSyncQuery
  }

  get balanceQuery(): OrchestratedQuery<Map<string, bigint>> | undefined {
    return this.balanceSyncQuery
  }

  get nativeBalanceQuery(): OrchestratedQuery<bigint> | undefined {
    return this.nativeBalanceSyncQuery
  }

  /**
   * Trigger balance update - called by OrchestratedQuery
   * This just fetches the data and returns it for the query system
   */
  async onUpdateBalancesTrigger(query: IOrchestratedQuerySender<Map<string, bigint>>): Promise<Map<string, bigint>> {
    const userAddress = this.walletStore.getConnectedAddress()

    if (!userAddress) {
      throw new Error("No connected wallet address")
    }

    const allTokens = this.tokenRepository.getAllTokensArray()
    if (allTokens.length === 0) {
      return new Map()
    }

    // Use the transport's updateBalances method with strategy
    // Strategy reads chunk size from query dynamically
    // Note: ETH (native token) is not in the repository, so it won't be included here

    return await this.tokenOnChainTransport.updateBalances(userAddress, allTokens, query, 50)
  }

  /**
   * Refresh balances for specific tokens
   * Useful after swaps or other transactions that affect specific token balances
   * More efficient than refreshing all tokens since we already have the TokenStore instances
   */
  async refreshTokenBalances(
    tokens: TokenStore[],
    preSwapBalances?: Map<string, bigint>,
    retryCount: number = 0,
  ): Promise<void> {
    const userAddress = this.walletStore.getConnectedAddress()

    if (!userAddress) {
      throw new Error("No connected wallet address")
    }

    if (tokens.length === 0) {
      return
    }

    // Use the existing transport method to fetch balances
    // Create a mock query sender for refresh (no query context, uses default chunk size)
    const mockQuerySender: IOrchestratedQuerySender<any> = {
      getRpcMetricsCollector: () => undefined,
      getChunkSize: () => undefined, // Will use default chunk size
    }
    const balances = await this.tokenOnChainTransport.updateBalances(userAddress, tokens, mockQuerySender, 50)

    // Update balances directly on the TokenStore instances we already have
    let balancesChanged = false
    runInAction(() => {
      tokens.forEach((token) => {
        const tokenAddressLower = token.address.toString().toLowerCase()
        const balance = balances.get(tokenAddressLower)

        if (balance !== undefined) {
          token.updateBalance(balance)

          // Check if balance changed from pre-swap value
          if (preSwapBalances) {
            const preSwapBalance = preSwapBalances.get(tokenAddressLower)
            if (preSwapBalance !== undefined && balance !== preSwapBalance) {
              balancesChanged = true
            }
          }
        }
      })
    })

    // If we have pre-swap balances and they haven't changed, retry after a delay
    if (preSwapBalances && !balancesChanged && retryCount < 3) {
      const retryDelay = 1000 * (retryCount + 1) // 1s, 2s, 3s delays
      await new Promise((resolve) => setTimeout(resolve, retryDelay))
      return this.refreshTokenBalances(tokens, preSwapBalances, retryCount + 1)
    }

    if (preSwapBalances && !balancesChanged && retryCount >= 3) {
      console.warn(
        `[TokenOrchestrator] Balances haven't changed from pre-swap values after ${retryCount + 1} attempts - RPC may not have indexed state yet`,
      )
    }
  }

  /**
   * Callback for when balance query succeeds
   */
  onLoadBalanceQuerySuccess(_sender: IOrchestratedQuerySender<Map<string, bigint>>, data: Map<string, bigint>): void {
    try {
      runInAction(() => {
        // Update each token's balance from the results

        data.forEach((balance, address) => {
          const token = this.tokenRepository.find(new LcAddress(address))
          if (token) {
            token.updateBalance(balance)
          }
        })
      })
    } catch (error) {
      console.error("Error processing balance data:", error)
    }
  }

  /**
   * Callback for when balance query fails
   */
  onLoadBalanceQueryFailed(_sender: IOrchestratedQuerySender<Map<string, bigint>>, error: Error): void {
    console.error("Balance query failed:", error)
  }

  /**
   * Trigger native balance update - called by OrchestratedQuery
   * This just fetches the data and returns it for the query system
   */
  async onUpdateNativeBalanceTrigger(query: IOrchestratedQuerySender<bigint>): Promise<bigint> {
    const userAddress = this.walletStore.getConnectedAddress()

    if (!userAddress) {
      throw new Error("No connected wallet address")
    }

    // Use the transport's getNativeBalance method
    return await this.tokenOnChainTransport.getNativeBalance(userAddress, query)
  }

  /**
   * Callback for when native balance query succeeds
   */
  onLoadNativeBalanceQuerySuccess(_sender: IOrchestratedQuerySender<bigint>, balance: bigint): void {
    try {
      ETH.updateBalance(balance)
    } catch (error) {
      console.error("Error updating native balance:", error)
    }
  }

  /**
   * Callback for when native balance query fails
   */
  onLoadNativeBalanceQueryFailed(_sender: IOrchestratedQuerySender<bigint>, error: Error): void {
    console.error("Native balance query failed:", error)
  }

  /**
   * Callback for when token query succeeds
   */
  onLoadTokenQuerySuccess(
    _sender: IOrchestratedQuerySender<Record<string, TokenApiDAO>>,
    data: Record<string, TokenApiDAO>,
  ): void {
    try {
      runInAction(() => {
        Object.values(data).forEach((tokenDAO) => {
          if (this.blacklistChecker(tokenDAO.address)) {
            return
          }

          let token = this.tokenRepository.find(tokenDAO.address)

          if (!token) {
            token = TokenApiFactory.createToken(tokenDAO)
            this.tokenRepository.add(token)
          } else {
            token.updateStaticData(tokenDAO)
          }
        })
      })
    } catch (error) {
      console.error("Error processing token data:", error)
    }
  }

  /**
   * Callback for when token query fails
   */
  onLoadTokenQueryFailed(_sender: IOrchestratedQuerySender<Record<string, TokenApiDAO>>, error: Error): void {
    console.error("Token query failed:", error)
  }

  /**
   * Set background sync interval for tokens with specific milliseconds (defaults to 60 seconds)
   */
  setTokensSyncInterval(intervalMs: number = 60_000): { success: boolean; message?: string } {
    if (this.tokenSyncQuery) {
      this.tokenSyncQuery.changeInterval(intervalMs)
      return { success: true }
    }
    return { success: false, message: "TokenOrchestrator: tokenSyncQuery is not initialized, cannot set interval." }
  }

  /**
   * Start background sync for tokens with specified interval (defaults to 60 seconds)
   */
  startTokensSync(intervalMs: number = 60_000): { success: boolean; message?: string } {
    if (this.tokenSyncQuery) {
      this.tokenSyncQuery.changeInterval(intervalMs)
      this.tokenSyncQuery.start()
      return { success: true }
    }
    return { success: false, message: "TokenOrchestrator: tokenSyncQuery is not initialized, cannot start sync." }
  }

  /**
   * Stop background sync for tokens
   */
  stopTokensSync(): { success: boolean; message?: string } {
    if (this.tokenSyncQuery) {
      this.tokenSyncQuery.stop()
      return { success: true }
    }
    return { success: false, message: "TokenOrchestrator: tokenSyncQuery is not initialized, cannot stop sync." }
  }

  /**
   * Force immediate tokens sync (get fresh data now)
   */
  forceTokensSync(): { success: boolean; message?: string } {
    if (this.tokenSyncQuery) {
      this.tokenSyncQuery.forceExecute()
      return { success: true }
    }
    return { success: false, message: "TokenOrchestrator: tokenSyncQuery is not initialized, cannot force sync." }
  }

  /**
   * Static token loader , adds all default tokens (i.e. required by CL Farms)
   * Static tokens are now TokenStore instances, so we add them directly
   */
  loadStaticTokens(): void {
    staticTokens.forEach((token) => {
      if (!this.tokenRepository.find(token.address)) {
        this.tokenRepository.add(token)
      }
    })
  }

  // Add new handler to capture balances when swap execution starts
  private handleSwapExecutionStarted(event: SwapExecutionStartedEvent): void {
    const { quote } = event
    const inputToken = quote.inputToken
    const outputToken = quote.outputToken

    // Capture current balances before swap
    const balances = new Map<string, bigint>()
    balances.set(inputToken.address.toString().toLowerCase(), inputToken.balance)
    balances.set(outputToken.address.toString().toLowerCase(), outputToken.balance)

    // Store using quote ID as key
    this.preSwapBalances.set(quote.quoteId, balances)
  }

  // Update handleSwapCompleted to pass pre-swap balances
  private async handleSwapCompleted(event: SwapCompletedEvent): Promise<void> {
    const { quote } = event

    // Get input and output token instances (we already have them from the quote)
    const inputToken = quote.inputToken
    const outputToken = quote.outputToken

    // Get pre-swap balances we captured when execution started
    const preSwapBalances = this.preSwapBalances.get(quote.quoteId)

    // Refresh balances for both tokens, passing pre-swap balances for validation
    try {
      await this.refreshTokenBalances([inputToken, outputToken], preSwapBalances)

      // Clean up pre-swap balances for this quote
      this.preSwapBalances.delete(quote.quoteId)
    } catch (error) {
      // Clean up pre-swap balances even on error
      this.preSwapBalances.delete(quote.quoteId)
      // Don't throw - this is a background refresh, shouldn't break the swap flow
    }
  }
}
